import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Two-layer carousel:
 *   • Inner track auto-scrolls via a CSS keyframe animation (buttery smooth)
 *   • Outer wrapper applies an imperative translate offset for arrow stepping
 *   • Both compose, so auto-scroll keeps running smoothly even after arrow clicks
 *   • Pauses on hover so users can read or tap a card
 *   • Click & drag (desktop) / swipe (mobile) to scrub
 */
export default function TickerCarousel({ speed = 40, testId, children }) {
  const items = React.Children.toArray(children);
  const trackRef = useRef(null);
  const halfRef = useRef(0);
  const cardRef = useRef(0);

  const [duration, setDuration] = useState(20);
  const [paused, setPaused] = useState(false);
  const [offset, setOffset] = useState(0);
  const [animateOffset, setAnimateOffset] = useState(true);

  // Measure the animated track to derive the CSS animation duration
  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const half = el.scrollWidth / 2;
    if (!half) return;
    halfRef.current = half;
    setDuration(half / speed);
    const firstCard = el.querySelector("[data-ticker-card]");
    if (firstCard) {
      const r = firstCard.getBoundingClientRect();
      const style = window.getComputedStyle(firstCard);
      cardRef.current =
        r.width +
        parseFloat(style.marginLeft || "0") +
        parseFloat(style.marginRight || "0");
    }
  }, [speed]);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("load", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("load", measure);
    };
  }, [measure]);

  // Wrap offset into a single cycle so it never drifts unbounded
  const wrap = (v) => {
    const half = halfRef.current;
    if (!half) return v;
    let n = v % half;
    if (n > 0) n -= half;
    return n;
  };

  // Arrow step — one card width per click. Direction: +1 = right (advance), -1 = left (back)
  const step = (dir) => {
    const card = cardRef.current || (window.innerWidth >= 768 ? 352 : 260);
    setAnimateOffset(true);
    setOffset((o) => wrap(o - dir * card));
  };

  // Pointer drag to scrub
  const drag = useRef({ startX: 0, startY: 0, startOffset: 0, pointerId: null, moved: false, startedAt: 0 });

  const onPointerDown = (e) => {
    if (e.target.closest("[data-ticker-arrow]")) return;
    // If the pointer down originated on a clickable link/button, do NOT activate drag-tracking —
    // even a few pixels of movement (mouse jitter, touch tremor) shouldn't swallow the click.
    if (e.target.closest("a, button")) {
      drag.current = { startX: 0, startY: 0, startOffset: offset, pointerId: null, moved: false, startedAt: 0 };
      return;
    }
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffset: offset,
      pointerId: e.pointerId,
      moved: false,
      startedAt: Date.now(),
    };
    setAnimateOffset(false);
    setPaused(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (drag.current.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    // Treat as a drag only when the user has clearly moved horizontally (8px) — small mouse jitter
    // should still allow the underlying <Link> click to fire.
    if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) drag.current.moved = true;
    if (drag.current.moved) setOffset(wrap(drag.current.startOffset + dx));
  };

  const endDrag = (e) => {
    if (drag.current.pointerId === null) return;
    e.currentTarget.releasePointerCapture?.(drag.current.pointerId);
    drag.current.pointerId = null;
    setAnimateOffset(true);
    setPaused(false);
  };

  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.stopPropagation();
      e.preventDefault();
      drag.current.moved = false;
    }
  };

  return (
    <div
      data-testid={testId}
      className="relative group"
    >
      <div
        className="ticker-fade marquee-wrap select-none"
        style={{ overflow: "hidden", touchAction: "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
      >
        <div
          style={{
            transform: `translateX(${offset}px)`,
            transition: animateOffset
              ? "transform 500ms cubic-bezier(.2,.7,.2,1)"
              : "none",
            willChange: "transform",
          }}
        >
          <div
            ref={trackRef}
            className="flex"
            style={{
              width: "max-content",
              animation: `ticker-scroll ${duration}s linear infinite`,
              animationPlayState: paused ? "paused" : "running",
              willChange: "transform",
            }}
          >
            {items.concat(items).map((child, i) => (
              <div key={`m-${i}`} data-ticker-card className="shrink-0">
                {React.cloneElement(child, {})}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Left arrow */}
      <button
        type="button"
        aria-label="Previous"
        data-ticker-arrow
        data-testid={testId ? `${testId}-prev` : undefined}
        onClick={() => step(-1)}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 md:h-12 md:w-12 rounded-full bg-white/95 border border-[var(--ia-border)] shadow-[0_8px_24px_-10px_rgba(0,0,0,.35)] flex items-center justify-center opacity-80 md:opacity-0 md:group-hover:opacity-100 hover:bg-white hover:border-black transition-all duration-300 hover:scale-110"
      >
        <ChevronLeft className="h-5 w-5 md:h-6 md:w-6 text-black" />
      </button>

      {/* Right arrow */}
      <button
        type="button"
        aria-label="Next"
        data-ticker-arrow
        data-testid={testId ? `${testId}-next` : undefined}
        onClick={() => step(1)}
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 md:h-12 md:w-12 rounded-full bg-white/95 border border-[var(--ia-border)] shadow-[0_8px_24px_-10px_rgba(0,0,0,.35)] flex items-center justify-center opacity-80 md:opacity-0 md:group-hover:opacity-100 hover:bg-white hover:border-black transition-all duration-300 hover:scale-110"
      >
        <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-black" />
      </button>
    </div>
  );
}
